import React, { useEffect, useState } from 'react';
import { FiChevronLeft, FiMoreHorizontal, FiPlus } from 'react-icons/fi';
import { DashboardSidebar } from '@/layouts/dashboard';
import { Link } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import { confirm } from '@/lib/confirm';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@/components/custom/empty-state';
import { useCourse } from '@/hooks/useCourse';
import { useTranslation } from 'react-i18next';
import { ModuleList, ModuleListItem, ModuleListTitle } from '@/components/custom/module';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';

export default function Sidebar({ moduleId, setSelectedModule, selectedModule, setManageModules, setModuleId }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { courses, getSimpleCourses } = useCourse();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentCourse, setCurrentCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [filteredModules, setFilteredModules] = useState([]);
  const client = useClient();

  const getModules = async () => {
    const { data } = await client.fetch('/learning_systems');
    setModules(data);
  };

  useEffect(() => {
    setFilteredModules(modules.filter(item => item['course-id'] == currentCourse));
  }, [currentCourse, moduleId]);

  const deleteModule = async item => {
    const { error } = await client.delete(`learning_systems/${item.id}`);
    if (error) {
      toast.error(t('toast.errorDeleteModule'));
    } else {
      toast.success(t('toast.successDeleteModule'));
      setModuleId(null);
      const newModules = modules.filter(m => m.id !== item.id);
      setModules(newModules);
      setFilteredModules(newModules.filter(item => item['course-id'] == currentCourse));
      setSelectedModule(null);
    }
  };

  const handleDelete = async item => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteModule'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteModule(item);
    }
  };

  useEffect(() => {
    getSimpleCourses();
  }, []);

  useEffect(() => {
    getModules();
  }, [courses]);

  useEffect(() => {
    setFilteredModules(
      filteredModules.filter(module => {
        if (searchTerm == '') {
          return module;
        }
        if (module.title != null && module.title.toLowerCase().includes(searchTerm.toLocaleLowerCase())) {
          return module;
        }
      })
    );
  }, [searchTerm]);

  useEffect(() => {
    if (moduleId && modules.length > 0) {
      const currentModule = modules?.find(item => item.id == moduleId);
      const currentCourse = currentModule?.['course-id'];
      setCurrentCourse(currentCourse);
      setSelectedModule(currentModule);
    }
  }, [modules]);

  return (
    <DashboardSidebar
      hasBrand={false}
      viewMode="create"
    >
      <nav className="notebook-sidebar__nav">
        <div className="filter-bar">
          <Link
            to="/lms"
            className="filter-bar__back"
          >
            <div className="card__icon-wrapper">
              <FiChevronLeft />
            </div>
            <span className="filter-bar__back-container">
              <span className="filter-bar__title">{t('textsCommon.modules')}</span>
            </span>
          </Link>
        </div>

        <div className="form__row">
          <label
            className="form__label"
            htmlFor="courseId"
          >
            {t('filter.labelSelectCourse')}
          </label>

          <select
            className="form__select"
            name="courseId"
            id="courseId"
            value={currentCourse}
            onChange={e => setCurrentCourse(e.target.value)}
          >
            <option value="blank">{t('filter.labelSelectCourse')}</option>
            {courses?.map(item => (
              <option value={item.id}>{item.title}</option>
            ))}
          </select>
        </div>

        <div className="form__row">
          <input
            aria-label="Pesquisar módulos"
            className="form__control form__control--search-with-icon"
            placeholder={t('textsCommon.research')}
            type="search"
            name="moduleSearch"
            id="moduleSearch"
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <button className="btn btn--outline-dashed btn--small btn--full mb-4">
          <Link to={'/lms/criar/modulo/curso/'}>
            <FiPlus className="btn__icon" /> {t('button.newModule')}
          </Link>
        </button>

        {currentCourse === null && (
          <EmptyState
            type="content"
            title="Nenhum curso selecionado."
            text="Selecione um acima."
          />
        )}

        {currentCourse !== null && !filteredModules.length > 0 && (
          <EmptyState
            type="content"
            title="Nenhum módulo encontrado."
            text="Crie um acima."
          />
        )}

        <ModuleList>
          {filteredModules.map(item => {
            return (
              <ModuleListItem
                key={item.id}
                active={selectedModule?.id == item.id}
                onClick={() => {
                  window.history.pushState({ moduleId: item.id }, '', `/lms/gerenciar/modulo/${item.id}`);
                  setSelectedModule(item);
                  setManageModules(false);
                }}
              >
                <ModuleListTitle>{item.title}</ModuleListTitle>

                <div onClick={e => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button">
                        <FiMoreHorizontal size={16} />
                      </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      align="end"
                      style={{ zIndex: 9999 }}
                    >
                      <DropdownMenuItem
                        onSelect={() => {
                          navigate(`/show-course/${item.id}/classroom`);
                        }}
                      >
                        {t('button.view')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          navigate(`/lms/criar/modulo/curso/${item.id}`);
                        }}
                      >
                        {t('button.edit')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          handleDelete(item);
                        }}
                      >
                        {t('button.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </ModuleListItem>
            );
          })}
        </ModuleList>
      </nav>
    </DashboardSidebar>
  );
}
